import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";

const PageTransitionContext = createContext(null);

/**
 * Transition key — used for route identity in pages that opt into readiness signaling.
 */
function getTransitionKey(pathname, search = "") {
  const parts = pathname.split("/").filter(Boolean);
  if (!parts.length) return pathname + search;

  const [root, second, third] = parts;

  if (root === "risk_center") {
    return pathname;
  }
  if (root === "IT") return pathname;
  if (root === "audit") return pathname + search;
  if (root === "survey") return pathname + search;
  if (root === "third_party") {
    if (second === "vendors" && third) return pathname + search;
    return pathname + search;
  }
  if (root === "controls") return pathname + search;
  if (root === "marketplace") return pathname + search;
  if (root === "profile") return pathname + search;
  if (root === "manage_risk_center" && second) return `${pathname}${search}`;
  if (root === "manage_sub_risk_center" && second) return `${pathname}${search}`;
  if (root === "control_overview" && second) return `${pathname}${search}`;
  if (root === "chat") return pathname + search;

  return pathname + search;
}

/**
 * Wraps authenticated content. Route changes swap children directly without a loading veil.
 */
export default function PageTransitionProvider({ children }) {
  const location = useLocation();
  const transitionKey = getTransitionKey(location.pathname, location.search);
  const routeKey = `${location.pathname}${location.search}`;

  const signalReady = useCallback(() => {}, []);

  const value = useMemo(
    () => ({ signalReady, routeKey, transitionKey }),
    [signalReady, routeKey, transitionKey]
  );

  return (
    <PageTransitionContext.Provider value={value}>
      <div className="relative flex min-h-0 min-w-0 w-full flex-1 flex-col overflow-hidden">
        {children}
      </div>
    </PageTransitionContext.Provider>
  );
}

export function usePageTransition() {
  const ctx = useContext(PageTransitionContext);
  if (!ctx) {
    throw new Error("usePageTransition must be used within PageTransitionProvider");
  }
  return ctx;
}

/** Kept for pages that call it; no-op while the transition veil is disabled. */
export function usePageReady() {}
